import { Capacitor } from "@capacitor/core"
import { InboxIcon, WorkflowIcon } from "lucide-react"
import { type ComponentProps, useEffect, useState } from "react"

import { RolePreviewControl } from "~/components/RolePreview"
import { ActivityExternalLink } from "~/components/ActivityExternalLink"
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarHeader,
	SidebarRail
} from "~/components/ui/sidebar"
import { ORBIT_COLOR, VERSION, VERSION_INFO } from "~/config"
import { useOrganization } from "~/hooks/useOrganization"
import { cn } from "~/lib/utils"
import { api } from "~/trpc/react"
import { OrbitLogo } from "../icons/logos"
import { NavSearch, NavSearchTrigger } from "./search"
import { NavMe } from "./me"
import { NavFavorites } from "./favorites"
import { OrgSelector } from "./orgSelector"
import { NavTeams } from "./teams"

function VersionMarker() {
	const [liveUpdateBundleId, setLiveUpdateBundleId] = useState<string | null>(
		null
	)

	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return
		void import("@capawesome/capacitor-live-update")
			.then(({ LiveUpdate }) => LiveUpdate.getCurrentBundle())
			.then(({ bundleId }) => setLiveUpdateBundleId(bundleId ?? null))
			.catch(() => undefined)
	}, [])

	const label = liveUpdateBundleId
		? `${VERSION} · ${liveUpdateBundleId}`
		: VERSION
	const title = [
		VERSION_INFO.version ? `Version: ${VERSION_INFO.version}` : null,
		VERSION_INFO.gitSha ? `Commit: ${VERSION_INFO.gitSha}` : null,
		liveUpdateBundleId ? `Live update: ${liveUpdateBundleId}` : null,
		VERSION_INFO.builtAt ? `Built: ${VERSION_INFO.builtAt}` : null
	]
		.filter(Boolean)
		.join("\n")

	const commitUrl = VERSION_INFO.gitSha
		? `https://github.com/buape/orbit/commit/${VERSION_INFO.gitSha}`
		: null

	if (commitUrl) {
		return (
			<ActivityExternalLink
				className="min-w-0 truncate text-text-small cursor-pointer hover:text-text-main"
				href={commitUrl}
				title={title || undefined}
			>
				{label}
			</ActivityExternalLink>
		)
	}

	return (
		<span
			className="min-w-0 truncate text-text-small"
			title={title || undefined}
		>
			{label}
		</span>
	)
}

export function OrbitSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
	const [isSearchOpen, setIsSearchOpen] = useState(false)
	const { organization } = useOrganization()
	const { data: teams } = api.user.getTeams.useQuery(
		{
			organizationId: organization?.id ?? ""
		},
		{
			enabled: !!organization?.id
		}
	)
	const { data: badges } = api.user.getBadges.useQuery(
		{
			organizationId: organization?.id ?? ""
		},
		{
			enabled: !!organization?.id,
			refetchInterval: 30_000
		}
	)
	return (
		<>
			<NavSearch open={isSearchOpen} onOpenChange={setIsSearchOpen} />
			<Sidebar
				{...props}
				className={cn(
					"border-0 bg-background *:data-[sidebar=sidebar]:bg-background ml-1",
					props.className
				)}
			>
				<SidebarHeader
					size="compact"
					className="select-none cursor-default border-0 px-2 md:items-start"
				>
					<OrgSelector />
				</SidebarHeader>
				<SidebarContent className="font-medium gap-2">
					<div className="min-w-0 px-2 empty:hidden">
						<RolePreviewControl />
					</div>
					<div className="min-w-0 px-2">
						<NavSearchTrigger onOpen={() => setIsSearchOpen(true)} />
					</div>
					<NavMe
						items={[
							{
								name: "My Inbox",
								slug: `/${organization?.slug}/me/inbox`,
								icon: InboxIcon,
								badge: badges?.inbox ?? 0
							},
							{
								name: "My Issues",
								slug: `/${organization?.slug}/me/issues`,
								icon: WorkflowIcon,
								badge: badges?.issues ?? 0
							}
						]}
					/>
					<NavFavorites />
					<NavTeams data={teams ?? []} />
				</SidebarContent>
				<SidebarFooter className="min-w-0 border-0 px-3 font-mono text-xs text-text-small select-none cursor-default bg-background">
					<OrbitLogo fill={ORBIT_COLOR} width={12} height={14} /> Orbit{" "}
					<VersionMarker />
				</SidebarFooter>
				<SidebarRail />
			</Sidebar>
		</>
	)
}
